// Where the visitor was heading before they were asked to sign in.
//
// A QR code can point straight at /studies, but a first-time visitor is sent
// off to IBM App ID, and possibly through sign-up and an emailed verification
// link, before they come back. That round trip leaves the origin and can even
// land in another tab, so the intent cannot ride along in React state or in
// the URL alone -- it is parked in localStorage until the visitor is signed in.

const KEY = 'saa.postLoginRedirect';
// A stale intent must not hijack a later, unrelated visit, but it does have to
// outlive the sign-up detour: the confirmation e-mail may sit unread for a
// while, and its token is good for 48 hours (service.py). Matching that window
// means the destination is never the part of the flow that expires first.
const MAX_AGE_MS = 48 * 60 * 60 * 1000;

// Only in-app destinations, and never the pages of the sign-in flow itself:
// remembering those would bounce the visitor straight back into the login.
const EXCLUDED = ['/login', '/signup', '/verify-signup'];

export function isSafePath(path) {
  if (typeof path !== 'string' || !path.startsWith('/')) return false;
  // "//host" and "/\host" are absolute URLs in disguise; they would send the
  // visitor somewhere else entirely.
  if (/^\/[/\\]/.test(path)) return false;
  const pathname = path.split(/[?#]/)[0];
  return !EXCLUDED.some(p => pathname === p || pathname.startsWith(`${p}/`));
}

export function rememberIntent(path) {
  if (!isSafePath(path)) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    // Private mode or a full quota; the visitor simply lands on the front page.
  }
}

/** The remembered destination, cleared as it is handed out. */
export function takeIntent() {
  let raw = null;
  try {
    raw = localStorage.getItem(KEY);
    localStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const { path, at } = JSON.parse(raw);
    if (!isSafePath(path)) return null;
    if (!at || Date.now() - at > MAX_AGE_MS) return null;
    return path;
  } catch {
    return null;
  }
}

/** The `next` parameter of a URL, when it names a destination we can honour. */
export function nextFromSearch(search) {
  try {
    const value = new URLSearchParams(search).get('next');
    return value && isSafePath(value) ? value : null;
  } catch {
    return null;
  }
}

/** `/login?next=<path>`, so the destination survives a plain page load too. */
export function loginPathFor(location) {
  const target = `${location.pathname}${location.search || ''}`;
  return isSafePath(target) ? `/login?next=${encodeURIComponent(target)}` : '/login';
}
